import {assert, assertAndContinue} from 'Asserts';
import {
  PipelineStepDataKind,
  PipelineStepStatusCode,
  PipelineStepType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isStepSucceeded} from 'pipelines/PipelineStepStatusUtils';
import {
  PipelineStep,
  PipelineStepData,
  PipelineStepRawData,
  PipelineViewGraphQL,
  pipelineStepRawDataIsKind,
  PipelineViewLineageNode,
} from 'pipelines/PipelineTypes';
import {getStepDataKindForRawData, ViewKindMatchContext} from 'pipelines/RawStepDataKindRegistry';

/**
 * Slim step output reference type.
 *
 * TODO(BNCH-145708): Replace with generated GraphQL fragment type after slimming PipelineViewFragment.
 */
interface SlimStepOutputReference {
  outputStepData: Pick<PipelineStepData, 'id'>;
  rawDataIndex: number | null;
}

/**
 * Resolve the full PipelineStep from the steps array for a lineage node.
 *
 * @param steps The pipeline's steps array (already fetched with full data)
 * @param lineageNode A lineage node with at least a step.id
 * @returns The full PipelineStep
 */
export function resolveLineageStep(
  steps: ReadonlyArray<PipelineStep>,
  lineageNode: {step: Pick<PipelineStep, 'id'>}
): PipelineStep {
  const step = steps.find((step) => step.id === lineageNode.step.id);
  assert(step != null, 'Expected to find step in pipeline steps array', {stepId: lineageNode.step.id});
  return step;
}

/**
 * Try to resolve the full PipelineStep from the steps array for a lineage node.
 * Returns null if the step cannot be found (instead of asserting).
 */
export function tryResolveLineageStep(
  steps: ReadonlyArray<PipelineStep> | undefined,
  lineageNode: {step: Pick<PipelineStep, 'id'>}
): PipelineStep | null {
  if (!steps || steps.length === 0) {
    return null;
  }
  return steps.find((step) => step.id === lineageNode.step.id) ?? null;
}

/**
 * Resolve the full PipelineStepData from the steps array for a lineage node's output reference.
 *
 * @param steps The pipeline's steps array
 * @param lineageNode A lineage node with step.id and stepOutputReference.outputStepData.id
 * @returns The full PipelineStepData
 */
export function resolveOutputStepData(
  steps: ReadonlyArray<PipelineStep>,
  lineageNode: {step: Pick<PipelineStep, 'id'>; stepOutputReference: SlimStepOutputReference}
): PipelineStepData {
  const stepDataId = lineageNode.stepOutputReference.outputStepData.id;
  // First try the lineage node's own step (common case).
  const step = resolveLineageStep(steps, lineageNode);
  const stepData = step.outputStepDatas.find((sd) => sd.id === stepDataId);
  if (stepData != null) {
    return stepData;
  }
  // The output step data may belong to a different step (e.g., TABLE_VIEW/CHART_VIEW nodes
  // reference a preceding step's output). Search all steps.
  for (const s of steps) {
    const found = s.outputStepDatas.find((sd) => sd.id === stepDataId);
    if (found != null) {
      return found;
    }
  }
  assert(false, 'Expected to find step data in pipeline steps', {
    stepId: lineageNode.step.id,
    stepDataId,
  });
}

interface SummarizedView {
  lineageLength: number;
  lastNode: {
    stepId: string;
    stepType: PipelineStepType;
    statusCode: PipelineStepStatusCode;
    hasStepOutputReference: boolean;
    rawDataIndex?: number | null;
    stepDataName?: string | undefined;
  } | null;
}

function summarizeLineageNode(
  lineageNode: PipelineViewLineageNode,
  steps: ReadonlyArray<PipelineStep>
): SummarizedView['lastNode'] {
  const step = resolveLineageStep(steps, lineageNode);
  const outputStepData =
    lineageNode.stepOutputReference != null
      ? resolveOutputStepData(steps, {
          step: lineageNode.step,
          stepOutputReference: lineageNode.stepOutputReference,
        })
      : null;
  return {
    stepId: lineageNode.step.id,
    stepType: step.stepType,
    statusCode: step.statusCode,
    hasStepOutputReference: lineageNode.stepOutputReference != null,
    rawDataIndex: lineageNode.stepOutputReference?.rawDataIndex,
    stepDataName: outputStepData?.name,
  };
}

/**
 * Summarize view information for asserts.
 */
export function summarizeView(view: PipelineViewGraphQL, steps: ReadonlyArray<PipelineStep>): SummarizedView {
  const lastNode = view.lineage[view.lineage.length - 1];
  return {
    lineageLength: view.lineage.length,
    lastNode: lastNode ? summarizeLineageNode(lastNode, steps) : null,
  };
}

interface HasViewsAndSteps {
  views: Array<PipelineViewGraphQL>;
  steps: ReadonlyArray<PipelineStep>;
}

/** Build a Map from step ID to PipelineStep for O(1) lookups. */
function buildStepMap(steps: ReadonlyArray<PipelineStep>): Map<string, PipelineStep> {
  return new Map(steps.map((step) => [step.id, step]));
}

/** Look up a step by ID from a precomputed Map, asserting it exists. */
function resolveLineageStepFromMap(
  stepMap: Map<string, PipelineStep>,
  lineageNode: {step: Pick<PipelineStep, 'id'>}
): PipelineStep {
  const step = stepMap.get(lineageNode.step.id);
  assert(step != null, 'Expected to find step in pipeline steps array', {stepId: lineageNode.step.id});
  return step;
}

/**
 * Return the child views for a given view.
 *
 * A child view is a view whose first node's step's incoming edge is from the last node of this view.
 */
export function getChildViewsForView(
  pipeline: HasViewsAndSteps,
  view: PipelineViewGraphQL
): Array<PipelineViewGraphQL> {
  if (view.lineage.length < 1) {
    return [];
  }

  const lastNode = view.lineage[view.lineage.length - 1];
  const stepOutputReference = lastNode.stepOutputReference;

  if (stepOutputReference == null) {
    return [];
  }

  const stepMap = buildStepMap(pipeline.steps);
  return pipeline.views.filter((otherView) => {
    const firstStep = resolveLineageStepFromMap(stepMap, otherView.lineage[0]);
    return firstStep.incomingEdges.some(
      (incomingEdge) =>
        incomingEdge.fromStep.id === lastNode.step.id &&
        incomingEdge.fromStepData.id === stepOutputReference.outputStepData.id &&
        (incomingEdge.fromStepDataIndex === stepOutputReference.rawDataIndex ||
          // Mimics similar logic in get_views.py; if the edge has an index set but the steps aren't in a completed state, we still want to match
          (stepOutputReference.rawDataIndex == null && incomingEdge.fromStepDataIndex === 0) ||
          // A null fromStepDataIndex indicates a multi-to-multi edge where all outputs flow into
          // the input. For unambiguous cases (single output), this correctly matches the parent.
          incomingEdge.fromStepDataIndex == null)
    );
  });
}

/**
 * Return the parent views for a given view.
 *
 * A parent view is a view whose last node's step has an outgoing edge to any step in this view.
 */
export function getParentViewsForView(
  pipeline: HasViewsAndSteps,
  view: PipelineViewGraphQL
): Array<PipelineViewGraphQL> {
  const viewStepIds = new Set(view.lineage.map((lineageNode) => lineageNode.step.id));
  if (viewStepIds.size === 0) {
    return [];
  }

  const stepMap = buildStepMap(pipeline.steps);
  return pipeline.views.filter((otherView) => {
    if (otherView.lineage.length === 0) {
      return false;
    }

    const lastStep = resolveLineageStepFromMap(stepMap, otherView.lineage[otherView.lineage.length - 1]);
    return lastStep.outgoingEdges.some((outgoingEdge) => viewStepIds.has(outgoingEdge.toStep.id));
  });
}

/**
 * Return if all parent views have succeeded or been halted, i.e. their last lineage node
 * has a status of SUCCEEDED or HALTED.
 */
export function allParentViewsSucceeded(pipeline: HasViewsAndSteps, view: PipelineViewGraphQL): boolean {
  return getParentViewsForView(pipeline, view).every((parentView) => {
    const lastStep = resolveLineageStep(pipeline.steps, parentView.lineage[parentView.lineage.length - 1]);
    return isStepSucceeded(lastStep.statusCode) || lastStep.statusCode === PipelineStepStatusCode.HALTED;
  });
}

/**
 * Get the output raw data for a view.
 *
 * Returns null if the view does not have an output data, which may occur for situations like:
 *  - The final step of the view hasn't been successfully run yet
 *  - The final step of the view doesn't produce outputs, e.g. the SEND_TO_DOCUMENT step
 *
 * When `dataKind` is provided, asserts that the output data is non-null and of the given kind.
 *
 */
export function getOutputDataForView(
  view: PipelineViewGraphQL,
  steps: ReadonlyArray<PipelineStep>
): PipelineStepRawData | null;
export function getOutputDataForView<K extends PipelineStepDataKind>(
  view: PipelineViewGraphQL,
  steps: ReadonlyArray<PipelineStep>,
  dataKind: K
): PipelineStepRawData<K>;
export function getOutputDataForView<K extends PipelineStepDataKind>(
  view: PipelineViewGraphQL,
  steps: ReadonlyArray<PipelineStep>,
  dataKind?: K
): PipelineStepRawData<K> | PipelineStepRawData | null {
  const lastNode = view.lineage[view.lineage.length - 1];
  if (lastNode == null) {
    if (dataKind != null) {
      assert(false, 'Expected non-empty view', {
        dataKind,
      });
    }
    return null;
  }
  if (dataKind != null) {
    return getOutputDataForLineageNode(lastNode, steps, dataKind);
  } else {
    return getOutputDataForLineageNode(lastNode, steps);
  }
}

export function getOutputDataForLineageNode(
  lineageNode: PipelineViewLineageNode,
  steps: ReadonlyArray<PipelineStep>
): PipelineStepRawData | null;
export function getOutputDataForLineageNode<K extends PipelineStepDataKind>(
  lineageNode: PipelineViewLineageNode,
  steps: ReadonlyArray<PipelineStep>,
  dataKind: K
): PipelineStepRawData<K>;
export function getOutputDataForLineageNode<K extends PipelineStepDataKind>(
  lineageNode: PipelineViewLineageNode,
  steps: ReadonlyArray<PipelineStep>,
  dataKind?: K
): PipelineStepRawData<K> | PipelineStepRawData | null {
  if (lineageNode.stepOutputReference == null || lineageNode.stepOutputReference.rawDataIndex == null) {
    assert(dataKind == null, 'Expected lineage node to have output data', {
      lineageNode: summarizeLineageNode(lineageNode, steps),
      dataKind,
    });
    return null;
  }
  const outputStepData = resolveOutputStepData(steps, {
    step: lineageNode.step,
    stepOutputReference: lineageNode.stepOutputReference,
  });
  const outputData = outputStepData.rawDatas[lineageNode.stepOutputReference.rawDataIndex];
  if (dataKind != null) {
    assert(
      pipelineStepRawDataIsKind(outputData, dataKind),
      'Expected lineageNode to have output data of specific kind',
      {lineageNode: summarizeLineageNode(lineageNode, steps), dataKind}
    );
    return outputData as PipelineStepRawData<K>;
  }
  return outputData;
}

/**
 * Return true if the view's output raw data is of the given kind.
 */
export function isViewOfKind(
  view: PipelineViewGraphQL,
  steps: ReadonlyArray<PipelineStep>,
  kind: PipelineStepDataKind
): boolean {
  const outputData = getOutputDataForView(view, steps);
  return outputData != null && pipelineStepRawDataIsKind(outputData, kind);
}

/**
 * Resolve the facts about a view that the raw-step-data-kind registry's
 * `matchesView` overrides need (its output data kind and last step type).
 */
export function buildViewKindMatchContext(
  view: PipelineViewGraphQL,
  steps: ReadonlyArray<PipelineStep>
): ViewKindMatchContext {
  const outputData = getOutputDataForView(view, steps);
  const outputDataKind = outputData != null ? (getStepDataKindForRawData(outputData) ?? null) : null;
  const lastNode = view.lineage[view.lineage.length - 1];
  const lastStepType = lastNode != null ? resolveLineageStep(steps, lastNode).stepType : null;
  return {outputDataKind, lastStepType};
}

/**
 * Return true if the view outputs a data frame.
 */
export function isDataFrameView(view: PipelineViewGraphQL, steps: ReadonlyArray<PipelineStep>): boolean {
  return isViewOfKind(view, steps, PipelineStepDataKind.DATASET);
}

/**
 * Return true if the view outputs a chart.
 */
export function isChartView(view: PipelineViewGraphQL, steps: ReadonlyArray<PipelineStep>): boolean {
  return isViewOfKind(view, steps, PipelineStepDataKind.CHART);
}

/**
 * Return true if the view outputs a file.
 */
export function isFileView(view: PipelineViewGraphQL, steps: ReadonlyArray<PipelineStep>): boolean {
  return isViewOfKind(view, steps, PipelineStepDataKind.FILE);
}

/**
 * Find the view whose last lineage node's output data matches the given ID.
 * Returns null if no view matches or the view's output data cannot be resolved.
 */
export function getViewForOutputDataId(
  pipeline: HasViewsAndSteps,
  outputDataId: string
): PipelineViewGraphQL | null {
  for (const view of pipeline.views) {
    const outputData = getOutputDataForView(view, pipeline.steps);
    if (outputData != null && outputData.id === outputDataId) {
      return view;
    }
  }
  return null;
}

/**
 * Return the client id for a given view.
 *
 * This function makes a best attempt at giving IDs to views. There are some major caveats.
 *
 * ### Issue with view ids
 *
 * Views fundamentally don't have an identity because they're data structures built on top of steps by the
 * backend. This means you can't access a view outside a given session, and they don't have stable IDs.
 *
 * ### View IDs
 *
 * Given the lack of identity, we still make a best attempt at composing an ID for views with this function.
 * We rely on the fact that the last node of a view can only be in 1 view ever. i.e., we never render the
 * same visual "view" twice in the UI.
 *
 * This means we can define the ID as the step id + the step output id (when present) + the raw data id (when present).
 *
 * ### Alternatives
 *
 * The best alternative would be to use the raw data index instead of the raw data id, which could be useful in some states,
 * but it seemed more reasonable to store state against a specific raw data. This is a trade-off that can
 * be revisited in the future.
 *
 * The other alternative is just storing the raw data id, but that's not always available.
 *
 * ### Caveats
 *
 * This ID has some weaknesses, but another way of thinking about it is that these cases are different views:
 * - It changes when the output of the last step in a view changes. This means it's good at storing state
 * against specific data but not a view.
 * - If we add a step at the end of a view, the id changes.
 *
 * If these caveats are unacceptable for your use case, you can use a different method generating the an ID
 * for a view.
 */
export function getDefaultClientIdForView(
  pipelineView: PipelineViewGraphQL,
  steps: ReadonlyArray<PipelineStep> = []
): string {
  if (!assertAndContinue(pipelineView.lineage.length > 0, 'Lineage must have at least one node')) {
    return 'DEFAULT_CLIENT_ID';
  }

  const lastNode = pipelineView.lineage[pipelineView.lineage.length - 1];
  const stepOutputReference = lastNode.stepOutputReference;

  const idPart1 = lastNode.step.id;
  const idPart2 = stepOutputReference == null ? '' : `-${stepOutputReference.outputStepData.id}`;
  const rawDataIndex = stepOutputReference?.rawDataIndex;
  let idPart3 = '';
  if (stepOutputReference != null && rawDataIndex != null && steps != null && steps.length > 0) {
    const outputStepData = resolveOutputStepData(steps, {
      step: lastNode.step,
      stepOutputReference,
    });
    // assertAndContinue is pre-existing here — it logs a warning but doesn't throw, since
    // getDefaultClientIdForView is called in rendering paths where a crash would be worse
    // than a degraded (shorter) client ID.
    if (assertAndContinue(rawDataIndex < outputStepData.rawDatas.length, 'rawDataIndex is out of bounds')) {
      idPart3 = `-${outputStepData.rawDatas[rawDataIndex].id}`;
    }
  }
  // When idPart3 is empty, it means either: the view's last step has no output reference,
  // no rawDataIndex, or rawDataIndex was out of bounds. The ID is still usable (step + stepData)
  // but won't distinguish between different raw data outputs of the same step.

  return `${idPart1}${idPart2}${idPart3}`;
}

/**
 * Extract the data frame ID and parent step info from a pipeline view.
 *
 * Returns null when the table is null (useful when callers handle nullable inputs).
 */
export function getParamsFromTable(
  table: PipelineViewGraphQL | null,
  steps: ReadonlyArray<PipelineStep>
): {dataFrameId: string; stepInfo: {stepId: string; stepDataName: string}} | null {
  if (table == null) {
    return null;
  }

  const lineageNode = table.lineage[table.lineage.length - 1];
  const outputStepData = resolveOutputStepData(steps, {
    step: lineageNode.step,
    stepOutputReference: lineageNode.stepOutputReference!,
  });
  return {
    dataFrameId: outputStepData.rawDatas[lineageNode.stepOutputReference!.rawDataIndex!].id,
    stepInfo: {
      stepId: lineageNode.step.id,
      stepDataName: outputStepData.name,
    },
  };
}
